/**
 * Service Type Mapping Models
 *
 * Unified configuration: default Type/Sub-Type/Item for all tickets,
 * with optional source-specific overrides.
 *
 * Cascading dependencies:
 * - Sub-Type requires Type to be set
 * - Item is independent (based on selected board, not Type/Sub-Type)
 */

/**
 * A single Type, Sub-Type, or Item field from the backend API.
 */
export interface ServiceTypeMappingApiField {
  id: number;
  display_text: string;
}

/**
 * Service Type Configuration
 *
 * Uses the same nested { id, display_text } structure as the backend API.
 * All fields are optional. Cascading: Sub-Type requires Type.
 * Item is independent and filtered by board, not by Type/Sub-Type.
 */
export interface ServiceTypeConfig {
  type: ServiceTypeMappingApiField | null;
  subtype: ServiceTypeMappingApiField | null;
  item: ServiceTypeMappingApiField | null;
}

/**
 * Maps a specific ticket source to a service type configuration
 */
export interface SourceMapping {
  id?: number;
  sourceValue: string;
  config: ServiceTypeConfig;
}

/**
 * Unified service type mapping configuration.
 *
 * - defaultConfig: applied to all tickets, or as fallback for unmapped sources
 * - mappings: optional source-specific overrides
 */
export interface ServiceTypeMappingConfig {
  defaultConfig: ServiceTypeConfig;
  mappings: SourceMapping[];
}

/**
 * Cyflare-managed list of ticket sources
 * Customers cannot add or modify this list
 */
export const TICKET_SOURCES = [
  'Stellar Cyber',
  'Sentinel One',
  'CrowdStrike EDR',
  'CrowdStrike XDR',
  'Chronicle',
  'Checkpoint',
  'Cyrisma',
  'Assure',
  'Polymer',
  'Vicarius',
] as const;

export type TicketSource = (typeof TICKET_SOURCES)[number];

/**
 * ConnectWise Type from API
 */
export interface ConnectWiseType {
  id: number;
  name: string;
}

/**
 * ConnectWise Sub-Type from API
 */
export interface ConnectWiseSubType {
  id: number;
  name: string;
  typeId: number;
}

/**
 * ConnectWise Item from API
 */
export interface ConnectWiseItem {
  id: number;
  name: string;
  boardId: number;
}

/**
 * Helper to create an empty ServiceTypeConfig
 */
export function createEmptyServiceTypeConfig(): ServiceTypeConfig {
  return {
    type: null,
    subtype: null,
    item: null,
  };
}

/**
 * Helper to check if a ServiceTypeConfig has any values set
 */
export function hasServiceTypeConfig(config: ServiceTypeConfig | null): boolean {
  if (!config) return false;
  return config.type !== null || config.subtype !== null || config.item !== null;
}

/**
 * Helper to create default ServiceTypeMappingConfig
 */
export function createDefaultMappingConfig(): ServiceTypeMappingConfig {
  return {
    defaultConfig: createEmptyServiceTypeConfig(),
    mappings: [],
  };
}

// ============================================================
// Backend API interfaces
// ============================================================

export interface ServiceTypeMappingResponse {
  pk: number;
  ticket_source: string;
  type: ServiceTypeMappingApiField | null;
  subtype: ServiceTypeMappingApiField | null;
  item: ServiceTypeMappingApiField | null;
}

export interface ServiceTypeMappingRequest {
  integration_details: number;
  ticket_source: string;
  type: ServiceTypeMappingApiField | null;
  subtype: ServiceTypeMappingApiField | null;
  item: ServiceTypeMappingApiField | null;
}
